import { useEffect, useState } from 'react';
import BlogNavigation from '../blog/components/BlogNavigation';
import TermsContent from './components/TermsContent';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function TermsOfServicePage() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.title = 'Terms of Service — JobOS | TechFraser Inc.';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'JobOS Terms of Service. Read the rules and guidelines governing your use of JobOS, including subscriptions, AI features, acceptable use, and your rights.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Terms of Service — JobOS', true);
    setMeta('og:url', `${SITE_URL}/terms`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/terms`);

    document.querySelectorAll('script[data-schema-page="terms"]').forEach(el => el.remove());

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Terms of Service — JobOS',
        url: `${SITE_URL}/terms`,
        description: 'JobOS Terms of Service. Rules and guidelines governing your use of JobOS.',
        publisher: { '@type': 'Organization', name: 'JobOS', url: SITE_URL },
        inLanguage: 'en-US',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: `${SITE_URL}/terms` },
        ],
      },
    ];

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'terms');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="terms"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A] text-gray-900 dark:text-[#F8FAFC]">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-800 z-[60]">
        <div
          className="h-full bg-gradient-to-r from-[#6366F1] to-[#818CF8] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <BlogNavigation />

      {/* Hero */}
      <div className="relative h-[400px] w-full overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/a6c7bf7eb20c54a610ad7823bc9e9830.jpg"
          alt="Terms of Service Hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-6 text-center w-full">
            <div className="inline-block px-4 py-1.5 bg-[#6366F1]/20 backdrop-blur-sm border border-[#6366F1]/30 rounded-full mb-5">
              <span className="text-[#818CF8] text-sm font-medium">Legal & Compliance</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight tracking-tighter">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-200 max-w-xl mx-auto font-medium leading-relaxed">
              The rules and guidelines governing your use of JobOS, operated by TechFraser Inc.
            </p>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        className={`fixed bottom-8 right-8 z-50 w-11 h-11 flex items-center justify-center rounded-full bg-[#6366F1] text-white shadow-lg shadow-[#6366F1]/30 hover:bg-[#4F46E5] hover:scale-110 transition-all duration-300 cursor-pointer whitespace-nowrap ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>

      {/* Meta bar */}
      <div className="w-full border-b border-gray-100 dark:border-white/5 bg-white dark:bg-[#0A0E1A]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <i className="ri-refresh-line text-[#6366F1]"></i>
            <span className="font-medium text-gray-700 dark:text-gray-300">Last updated:</span>
            <time dateTime="2026-03-05" className="font-mono">March 5, 2026</time>
          </div>
          <div className="w-px h-4 bg-gray-200 dark:bg-white/10 hidden sm:block"></div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
              <i className="ri-shield-check-line text-xs"></i>
              Canadian Law
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] dark:text-[#818CF8] text-xs font-bold">
              <i className="ri-building-line text-xs"></i>
              TechFraser Inc.
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-bold">
              <i className="ri-flag-line text-xs"></i>
              Ontario, Canada
            </span>
          </div>
          <div className="ml-auto text-xs text-gray-400 dark:text-gray-500 font-mono hidden md:block">
            Effective Date: March 5, 2026
          </div>
        </div>
      </div>

      {/* Main content */}
      <main>
        <TermsContent />
      </main>

      <Footer />
    </div>
  );
}
